interface ProgressBarCommon {
  /** Accessible label; a determinate bar appends the live percentage. */
  label?: string;
  className?: string;
  /**
   * Also announce the label through a visually hidden `<output>` (implicit
   * `role="status"`, polite live region). A name on a role-less div is not
   * announced, so a bar that replaces a status Spinner opts in here to keep
   * screen readers informed. Off by default: most bars sit beside live text.
   * The LABEL only, never the percentage: a polling bar would re-announce
   * every tick. The percentage stays readable in the track's `aria-label`.
   */
  live?: boolean;
  /** `data-testid` forwarded to the track. */
  testId?: string;
}

/** Work whose completion fraction IS known (e.g. contacts processed / total). */
interface DeterminateProgressBarProps extends ProgressBarCommon {
  indeterminate?: false;
  /** Completed units. Clamped to `[0, max]`. */
  value: number;
  /** Total units. Values ≤ 0 render an empty track. */
  max: number;
}

/**
 * Work that is happening but has no fraction to report — an upload with no
 * byte events, a server step with no counter. A percentage here would be
 * invented, so none is rendered or announced.
 */
interface IndeterminateProgressBarProps extends ProgressBarCommon {
  indeterminate: true;
  value?: never;
  max?: never;
}

export type ProgressBarProps = DeterminateProgressBarProps | IndeterminateProgressBarProps;

// `border`: the hairline token, which `tokens.css` names for progress tracks.
// `surface-raised` was ~1.1:1 on the light page and card (PR #1016 iter 2,
// C M-F); `border` is about 1.3:1 on light and 1.4:1 on dark against both,
// and the fill (`accent`) stays well clear of it. Pinned in the test.
const TRACK = "h-1.5 w-full overflow-hidden rounded-full bg-border";

/**
 * ProgressBar — a horizontal track for long-running work.
 *
 * Determinate: the fill width is the live percentage. That width is the ONLY
 * inline style, the sanctioned runtime-value case (Tailwind cannot express an
 * arbitrary runtime `%`). Indeterminate: a third-width segment slides across
 * the track via the `progress-indeterminate` animation token, and stops for
 * users who prefer reduced motion.
 *
 * Accessibility follows NewImportPage's sanctioned pattern: a labelled track
 * rather than `role="progressbar"` (which biome flags as an interactive widget
 * needing a keyboard tab stop — wrong for a read-only indicator). The live
 * percentage rides in the `aria-label`; an indeterminate bar announces the
 * label alone.
 */
export function ProgressBar(props: ProgressBarProps) {
  const { label = "Progress", className, live = false, testId } = props;
  const classes = [TRACK, className].filter(Boolean).join(" ");

  if (props.indeterminate) {
    return (
      <>
        <div className={classes} aria-label={label} data-testid={testId}>
          <div className="h-full w-1/3 rounded-full bg-accent animate-progress-indeterminate motion-reduce:animate-none" />
        </div>
        {live && <LiveText text={label} />}
      </>
    );
  }

  const safeMax = props.max > 0 ? props.max : 0;
  const clamped = safeMax > 0 ? Math.min(Math.max(props.value, 0), safeMax) : 0;
  const pct = safeMax > 0 ? Math.round((clamped / safeMax) * 100) : 0;

  const name = `${label}: ${pct}%`;
  return (
    <>
      <div className={classes} aria-label={name} data-testid={testId}>
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {live && <LiveText text={label} />}
    </>
  );
}

/**
 * Rendered AFTER the track and `sr-only` (absolutely positioned), so it takes
 * no flex slot or gap in the caller's layout and the track stays the first
 * element.
 */
function LiveText({ text }: { text: string }) {
  return (
    <output aria-live="polite" className="sr-only">
      {text}
    </output>
  );
}
